import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Header, Icon, Loader, Table } from 'semantic-ui-react';
import agent from '../../../app/api/agent';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { dayWiseHoursReport, hoursMissingEmployeeDetailsReport } from '../../../app/models/Restoration';

interface GroupedReportData {
  [managerName: string]: hoursMissingEmployeeDetailsReport[];
}

const HoursMissingEmployeeDetails = () => {
  const [reportData, setReportData] = useState<GroupedReportData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedManagers, setExpandedManagers] = useState<Set<string>>(new Set());
  const currentDate = new Date();
  const [startDate, setStartDate] = useState(() => moment(currentDate).format('MM-DD-YYYY'));
  const [endDate, setEndDate] = useState(() => moment(currentDate).format('MM-DD-YYYY'));
  const [dayWiseData, setDayWiseData] = useState<dayWiseHoursReport[]>([])

  const setDate = (data: Date, type: 'start' | 'end') => {
    const formattedDate = moment(data).format('MM-DD-YYYY');
    if (type === 'start') {
      setStartDate(formattedDate);
    } else {
      setEndDate(formattedDate);
    }
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      const response = await agent.Restoration.hoursMissingEmployeeDetails(startDate, endDate);
      const groupedData = response.data.reduce((acc: GroupedReportData, curr: any) => {
        const managerName = curr.manager_Name;
        if (!acc[managerName]) {
          acc[managerName] = [];
        }
        acc[managerName].push(curr);
        return acc;
      }, {} as GroupedReportData);
      const response1 = await agent.Restoration.dayWiseHours(startDate, endDate, false  , true)
      setDayWiseData(response1.data);
      setReportData(groupedData);
    } catch (error) {
      toast.error('Error fetching report data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    
    const headers = ["Manager Name", "Cluster Name", `${endDate}`, "MTD Count"];
    csvContent += headers.join(",") + "\r\n";
  
    for (const managerName in reportData) {
      // eslint-disable-next-line no-loop-func
      reportData[managerName].forEach(data => {
        const row = [data.manager_Name, data.cluster_Name, data.day_Count, data.mTD_Count];
        csvContent += row.join(",") + "\r\n";
      });
    }
    
    csvContent += '**MTD - Monthly Till Date\r\n'
    csvContent += "\r\n";
    csvContent += 'Hours Missing Employee Details:\r\n'
  
    const dayWiseHeaders = ["Date", "Manager", "Lead", "Employee ID", "Employee Name", "Hours", "Daily Hour Achieved"];
    csvContent += dayWiseHeaders.join(",") + "\r\n";
  
    dayWiseData.forEach(data => {
      const dayWiseRow = [data.date, data.processLead, data.clusterName, data.employee_ID, data.employee_Name, data.hour, "No"];
      csvContent += dayWiseRow.join(",") + "\r\n";
    });
  
    const filename = `Hours Missing Employee Details Report - ${startDate}_${endDate}.csv`;
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleManager = (managerName: string) => {
    setExpandedManagers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(managerName)) {
        newSet.delete(managerName);
      } else {
        newSet.add(managerName);
      }
      return newSet;
    });
  };

  const dayCount = moment(endDate).format('DD-MMM-YYYY');

  return (
    <div className="title_wise_cutlistreport">
      <Header as='h2'>Hours Missing Employee Details</Header>
      <div style={{ display: "flex", alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ marginRight: "10px" }}>
          <label style={{ fontWeight: "bold" }}>From</label>
          <DatePicker
            selected={startDate ? moment(startDate, 'MM-DD-YYYY').toDate() : null}
            className="date-picker-style-m0"
            maxDate={moment().toDate()}
            onChange={(date: Date) => setDate(date, 'start')}
          />
        </div>
        <div style={{ marginRight: "10px" }}>
          <label style={{ fontWeight: "bold" }}>To</label>
          <DatePicker
            selected={endDate ? moment(endDate, 'MM-DD-YYYY').toDate() : null}
            className="date-picker-style-m0"
            maxDate={moment().toDate()}
            onChange={(date: Date) => setDate(date, 'end')}
          />
        </div>
            <div style={{ marginRight: "10px", alignSelf:'end' }}>
                  <Button onClick={handleViewReport} primary disabled={loading}>View Report</Button>
                  <Button onClick={handleDownloadReport} style={{ marginLeft: '5px', width: '50px' }} disabled={loading}>
                    <Icon name='download' />
                  </Button>
            </div>
      </div>
      {loading ? (
        <Loader active inline='centered' />
      ) : (
        <>
         <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ textAlign: 'center' }}>Manager Name</Table.HeaderCell>
                <Table.HeaderCell style={{ textAlign: 'center' }}>Cluster Name</Table.HeaderCell>
                <Table.HeaderCell style={{ textAlign: 'center' }}>{dayCount}</Table.HeaderCell>
                <Table.HeaderCell style={{ textAlign: 'center' }}>MTD Count</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.keys(reportData).length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">No data available.</Table.Cell>
                </Table.Row>
              ) : (
                Object.keys(reportData).map((managerName) => {
                  const managerData = reportData[managerName];
                  const groupTotal = managerData.reduce((sum, data) => sum + data.day_Count, 0);
                  const groupedTotal = managerData.reduce((sum, data) => sum + data.mTD_Count, 0);
                  const isExpanded = expandedManagers.has(managerName);

                  return (
                    <React.Fragment key={managerName}>
                      <Table.Row>
                        <Table.Cell>
                          {managerName}
                          <Icon name={isExpanded ? 'minus square outline' : 'plus square outline'} onClick={() => toggleManager(managerName)} color='blue' style={{ marginLeft: '5px' }} />
                        </Table.Cell>
                        <Table.Cell textAlign="right" style={{ fontWeight: 'bold' }}>Group Total:</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center', fontWeight: 'bold'}}>{groupTotal}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center', fontWeight: 'bold' }}>{groupedTotal}</Table.Cell>
                      </Table.Row>
                      {isExpanded && managerData.map((data, subIndex) => (
                        <Table.Row key={subIndex}>
                          <Table.Cell></Table.Cell>
                          <Table.Cell>{data.cluster_Name}</Table.Cell>
                          <Table.Cell style={{ textAlign: 'center' }}>{data.day_Count}</Table.Cell>
                          <Table.Cell style={{ textAlign: 'center' }}>{data.mTD_Count}</Table.Cell>
                        </Table.Row>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="2" textAlign="right" style={{ fontWeight: 'bold' }}>Grand Total:</Table.HeaderCell>
                <Table.HeaderCell colSpan="1" style={{ textAlign: 'center' }}>
                  <b>{Object.values(reportData).flat().reduce((total, data) => total + data.day_Count, 0)}</b>
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="1" style={{ textAlign: 'center' }}>
                  <b>{Object.values(reportData).flat().reduce((total, data) => total + data.mTD_Count, 0)}</b>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
          <Header as='h4'>**MTD - Monthly Till Date </Header>
          <div>
          {endDate && (
            <><Header style={{textAlign: 'left', fontWeight:'bold'}}>Hours Missing Employee Details:</Header>
            <Table compact celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Date</Table.HeaderCell>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Manager</Table.HeaderCell>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Lead</Table.HeaderCell>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Employee ID</Table.HeaderCell>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Employee Name</Table.HeaderCell>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Hours</Table.HeaderCell>
                      <Table.HeaderCell style={{ textAlign: 'center' }}>Daily Hour Achieved</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {dayWiseData.length === 0 && (
                      <Table.Row>
                        <Table.Cell colSpan={15} textAlign="center">
                          No data available.
                        </Table.Cell>
                      </Table.Row>
                    )}
                    {dayWiseData.length > 0 && dayWiseData.map((data, index) => (
                      <Table.Row key={index}>
                        <Table.Cell style={{ textAlign: 'center' }}>{data.date}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center' }}>{data.processLead}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center' }}>{data.clusterName}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center' }}>{data.employee_ID}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center' }}>{data.employee_Name}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center' }}>{data.hour}</Table.Cell>
                        <Table.Cell style={{ textAlign: 'center' }}>No</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table></>
           )}  
          </div>
        </>
      )}
    </div>
  );
};

export default HoursMissingEmployeeDetails;
